/**
 * Q1/Q2 → scan mapping, and the union of scans it can call for (#726, #736).
 *
 * Each answer maps to EXACTLY ONE scan, and every one of them is ranked
 * top-down (higher is better) — no "or", no multi-scan combining, no inverted
 * directions. Because the direction is uniform there is nothing per-scan to
 * record: `scanRanking` normalizes every axis the same way, so a direction
 * field here would be six copies of the same value waiting to be contradicted.
 *
 * #726 prefetched a wider ten-scan union drawn from an earlier draft of the
 * mapping (`FiscalHealthScore`, `ReturnOnEquity`, `TechnicalScore`,
 * `CapitalAdequacyRatio`). #736 settled the mapping at one scan per answer, so
 * the union is now the six below and the prefetch narrows with it — the four
 * dropped scans are no longer read by anything.
 *
 * `SCORING_SCANS` is DERIVED from the two maps rather than written out, so the
 * prefetch cannot fall out of step with the ranking: a mapping change is a
 * prefetch change by construction.
 *
 * Deliberately NOT derived from `HASHTAG_FILTER_CONFIG` the way
 * `HASHTAG_SCAN_MAP` is: these are scoring inputs, not filter conditions, and
 * only three of them (`GrowthScore`, `TotalScore`, `NetSalesChange`) happen to
 * also be some hashtag's scan. For the same reason they are absent from
 * `VALID_SCAN_NAMES`, which is the whitelist the hashtag config is checked
 * against — adding them there would assert something untrue about the filter
 * config.
 *
 * Names verified against Syphon's `ScanNames` enum (swagger.yaml @ master).
 * Endpoint: GET /market/scans/{scanName}
 *
 * @see src/utils/scanRanking.js for the ranking that consumes the mapping
 * @see src/constants/hashtagScanMap.js for the filter-side equivalent
 */

/**
 * Q1 — investment goal and horizon.
 *
 * @type {Readonly<Object<string, string>>}
 */
export const Q1_SCAN_BY_ANSWER = Object.freeze({
  // A — long-term
  A: 'DividendYield',
  // B — mid-term
  B: 'GrowthScore',
  // C — short-term. The PRD calls this one "Revision Score"; Syphon maps
  // `SC_REVISION_SCORE → marketTrendScore`, so it is the same scan.
  C: 'MarketTrendScore'
});

/**
 * Q2 — risk and return appetite.
 *
 * @type {Readonly<Object<string, string>>}
 */
export const Q2_SCAN_BY_ANSWER = Object.freeze({
  // A — low risk
  A: 'SizeScore',
  // B — mid risk
  B: 'TotalScore',
  // C — high risk
  C: 'NetSalesChange'
});

/** The per-question maps, keyed by the question id the answers use. */
const SCAN_BY_QUESTION = Object.freeze({
  q1: Q1_SCAN_BY_ANSWER,
  q2: Q2_SCAN_BY_ANSWER
});

/**
 * Resolves a `{ q1, q2 }` answer pair to the two scans that rank it.
 *
 * Tolerates a missing or unrecognised code rather than throwing: the results
 * step can render mid-flow (Q1 answered, Q2 not yet), and a persisted session
 * from an older build could carry a code that no longer exists. An unresolved
 * axis comes back `null`, and `rankPool` drops it from the composite.
 *
 * @param {{q1: ?string, q2: ?string}} [answers] - Option codes from
 *   `state.scans.aiScannerAnswers`.
 * @returns {{q1: ?string, q2: ?string}} Scan names, `null` per unresolved axis.
 */
export function getAnswerScans(answers) {
  const codes = answers || {};
  // `hasOwnProperty`, not a truthiness check on the lookup: answer codes reach
  // here from a persisted session, and a plain `map[code]` would resolve
  // `'constructor'` or `'toString'` to something inherited and truthy — a
  // "scan name" that is a function, which `readScanValue` would then lowercase
  // into a cache miss on every symbol instead of dropping the axis.
  const resolve = (questionId, code) => {
    const map = SCAN_BY_QUESTION[questionId];
    return Object.prototype.hasOwnProperty.call(map, code) ? map[code] : null;
  };

  return {
    q1: resolve('q1', codes.q1),
    q2: resolve('q2', codes.q2)
  };
}

/**
 * Every scan any answer pair can call for, de-duplicated, in Q1-then-Q2
 * declaration order. Prefetched when the member taps 「NNN 件を表示」 so the
 * ranking can order the pool without waiting on requests at the moment Q2 is
 * answered.
 *
 * @type {ReadonlyArray<string>}
 */
const SCORING_SCANS = Object.freeze(
  Array.from(
    new Set(
      Object.values(Q1_SCAN_BY_ANSWER).concat(Object.values(Q2_SCAN_BY_ANSWER))
    )
  )
);

export default SCORING_SCANS;
